interface SortConfig<T> {
    label: string;
    Icon: React.ReactElement;
    handler: (a: T, b: T) => number;
}

export class SortConfigBuilder<T> {
    private config: Record<string, SortConfig<T>> = {};

    addDateField(
        fieldName: keyof T,
        labels: { asc: string; desc: string },
        icons: { asc: React.ReactElement; desc: React.ReactElement }
    ) {
        const ascKey = `${String(fieldName)}_asc`;
        const descKey = `${String(fieldName)}_desc`;

        this.config[ascKey] = {
            label: labels.asc,
            Icon: icons.asc,
            handler: (a: T, b: T) => {
                const valueA = a[fieldName];
                const valueB = b[fieldName];

                if (!valueA && !valueB) return 0;
                if (!valueA) return 1;
                if (!valueB) return -1;

                const dateA = new Date(valueA as number|string|Date).getTime();
                const dateB = new Date(valueB as number|string|Date).getTime();

                if (isNaN(dateA) && isNaN(dateB)) return 0;
                if (isNaN(dateA)) return 1;
                if (isNaN(dateB)) return -1;

                return dateA - dateB;
            }
        };

        this.config[descKey] = {
            label: labels.desc,
            Icon: icons.desc,
            handler: (a: T, b: T) => -this.config[ascKey].handler(a, b)
        };

        return this;
    }

    addStringField(
        fieldName: keyof T,
        labels: { asc: string; desc: string },
        icons: { asc: React.ReactElement; desc: React.ReactElement }
    ) {
        const ascKey = `${String(fieldName)}_asc`;
        const descKey = `${String(fieldName)}_desc`;

        this.config[ascKey] = {
            label: labels.asc,
            Icon: icons.asc,
            handler: (a: T, b: T) => {
                const strA = (a[fieldName] as string) || '';
                const strB = (b[fieldName] as string) || '';
                return strA.localeCompare(strB);
            }
        };

        this.config[descKey] = {
            label: labels.desc,
            Icon: icons.desc,
            handler: (a: T, b: T) => -this.config[ascKey].handler(a, b)
        };

        return this;
    }

    addNumberField(
        fieldName: keyof T,
        labels: { asc: string; desc: string },
        icons: { asc: React.ReactElement; desc: React.ReactElement }
    ) {
        const ascKey = `${String(fieldName)}_asc`;
        const descKey = `${String(fieldName)}_desc`;

        this.config[ascKey] = {
            label: labels.asc,
            Icon: icons.asc,
            handler: (a: T, b: T) => {
                const numA = (a[fieldName] as number) || 0;
                const numB = (b[fieldName] as number) || 0;
                return numA - numB;
            }
        };

        this.config[descKey] = {
            label: labels.desc,
            Icon: icons.desc,
            handler: (a: T, b: T) => {
                const numA = (a[fieldName] as number) || 0;
                const numB = (b[fieldName] as number) || 0;
                return numB - numA;
            }
        };

        return this;
    }

    addCustom(
        key: string,
        label: string,
        icon: React.ReactElement,
        handler: (a: T, b: T) => number
    ) {
        this.config[key] = {
            label,
            Icon: icon,
            handler
        };

        return this;
    }

    build() {
        return this.config;
    }
}

export function createSortConfig<T>() {
  return new SortConfigBuilder<T>();
}